import { Component, OnInit, OnDestroy } from '@angular/core';
import { ICE_SERVERS } from '../../utils/webrtc-config';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AgGridListComponent } from '../../components/ag-grid-list/ag-grid-list.component';
import { HeaderComponent } from '../../components/header/header.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { ListActionButtonsComponent } from '../../components/list-action-buttons/list-action-buttons.component';
import { GlobalService } from '../../services/global.service';
import { DropdownService } from '../../services/dropdown.service';
import { LiveProctorService } from '../../services/live-proctor.service';
import { HttpService } from '../../services/http.service';

@Component({
  selector: 'app-live-proctor-list-page',
  standalone: true,
  imports: [CommonModule, FormsModule, AgGridListComponent, HeaderComponent, SelectInputComponent, ListActionButtonsComponent],
  templateUrl: './live-proctor-list-page.component.html',
})
export class LiveProctorListPageComponent implements OnInit, OnDestroy {

  page_name = 'Live Proctor';
  view_mode: 'grid' | 'list' = 'grid';
  params: any = {};
  items: any = { data: [], count: 0 };

  university_options: any[] = [];
  // Full exam list from the dropdown service; `exam_options` is the university-scoped view of it.
  all_exam_options: any[] = [];
  exam_options: any[] = [];
  subject_options: any[] = [];
  role_options: any[] = [];

  private ws: WebSocket | null = null;
  streams: Map<number, MediaStream> = new Map();
  peer_connections: Map<number, RTCPeerConnection> = new Map();
  admin_user_id: number = 0;

  columns: any[] = [
    {
      headerName: 'User ID', flex: 1.5, minWidth: 140,
      cellRenderer: (p: any) => {
        const uid = p.data?.user_id || p.data?.User?.id || '-';
        const triggered = p.data?.ai_triggered;
        const icon = triggered ? `<i class="bx bx-error-circle" style="color:#F59E0B;margin-right:4px;font-size:15px"></i>` : '';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:500;color:#212121">${icon}${uid}</div>`;
      }
    },
    {
      headerName: 'Faculty Name', flex: 2, minWidth: 160,
      cellRenderer: (p: any) => {
        const name = p.data?.User?.name || '-';
        const role = p.data?.User?.role || p.data?.role_name || '';
        return `<div style="display:flex;flex-direction:column;justify-content:center;height:100%;gap:1px">
          <span style="font-size:14px;font-weight:500;color:#212121;line-height:1.3">${name}</span>
          ${role ? `<span style="font-size:12px;color:#6b7280;line-height:1.3">${role}</span>` : ''}
        </div>`;
      }
    },
    {
      headerName: 'Contact', flex: 2, minWidth: 160,
      cellRenderer: (p: any) => {
        const contact = p.data?.User?.contact ? `+91${p.data.User.contact}` : '-';
        const email = p.data?.User?.email || '';
        return `<div style="display:flex;flex-direction:column;justify-content:center;height:100%;gap:1px">
          <span style="font-size:14px;color:#212121;line-height:1.3">${contact}</span>
          ${email ? `<span style="font-size:12px;color:#6b7280;line-height:1.3">${email}</span>` : ''}
        </div>`;
      }
    },
    {
      headerName: 'Session Start Time', flex: 2, minWidth: 160,
      valueGetter: (p: any) => this.format_date(p.data?.session_start || p.data?.created_at)
    },
    {
      headerName: 'Status', flex: 1.2, minWidth: 110,
      cellRenderer: (p: any) => {
        const status = (p.data?.status || '').toLowerCase();
        const online = status === 'online';
        const color = online ? '#16A34A' : '#EF4444';
        const label = online ? 'ONLINE' : 'OFFLINE';
        return `<div style="display:flex;align-items:center;height:100%;font-size:13px;font-weight:700;color:${color}">${label}</div>`;
      }
    },
  ];

  actions(row: any) {
    return [
      { type: 'button', text: '', icon: 'bx bx-show', function: (r: any) => this.navigate_to_detail(r?.user_id || r?.User?.id) }
    ];
  }

  constructor(
    public gs: GlobalService,
    public dds: DropdownService,
    public lps: LiveProctorService,
    public hs: HttpService,
    public router: Router,
  ) {}

  async ngOnInit() {
    // Access Level: lock the filters this user cannot change before the first fetch.
    this.gs.apply_access_scope_to_params(this.params);
    this.admin_user_id = this.gs.user_data?.id || 0;
    await this.dds.get_dropdown_list(['university', 'exam', 'role']);
    this.university_options = this.dds.dropdown_list?.university_list || [];
    this.all_exam_options = this.dds.dropdown_list?.exam_list || [];
    this.filter_exam_options();
    this.subject_options = this.dds.dropdown_list?.subject_list || [];
    this.role_options = this.dds.dropdown_list?.role_list || [];
    if (this.view_mode === 'list') {
      this.columns.push({
        headerName: 'Actions', sortable: false, filter: false,
        cellRenderer: ListActionButtonsComponent,
        headerClass: 'custom-header-center',
        cellStyle: { 'justify-content': 'center' },
        cellRendererParams: { actions: this.actions.bind(this) },
        editable: false, pinned: 'right', width: 90, minWidth: 90, maxWidth: 90,
      });
    }
    await this.get_list();
    this.connect_websocket();
  }

  ngOnDestroy() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.peer_connections.forEach(pc => pc.close());
    this.peer_connections.clear();
    this.streams.forEach(s => s.getTracks().forEach(t => t.stop()));
    this.streams.clear();
  }

  connect_websocket() {
    const base_ws = this.hs.base_url.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const ws_protocol = this.hs.base_url.startsWith('https') ? 'wss' : 'ws';
    this.ws = new WebSocket(`${ws_protocol}://${base_ws}/ws/live-proctor`);

    this.ws.onopen = () => {
      this.ws?.send(JSON.stringify({ type: 'join_admin', user_id: this.admin_user_id }));
    };

    this.ws.onmessage = async (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'active_evaluators') {
          // BE sends this on join_admin — request streams for all online evaluators
          (msg.user_ids as string[]).forEach(uid => {
            const numId = Number(uid);
            if (!this.peer_connections.has(numId)) this.request_stream(numId);
          });
        } else if (msg.type === 'evaluator_online') {
          const existing = this.items.data.find((d: any) => (d.user_id || d.User?.id) === Number(msg.user_id));
          if (existing) {
            existing.status = 'online';
            this.items = { ...this.items };
          } else {
            await this.get_list();
          }
          // Also request the stream for the newly joined evaluator
          this.sync_streams();
        } else if (msg.type === 'evaluator_offline') {
          const existing = this.items.data.find((d: any) => (d.user_id || d.User?.id) === Number(msg.user_id));
          if (existing) {
            existing.status = 'offline';
            this.items = { ...this.items };
            const video_el = document.getElementById('video-' + msg.user_id) as HTMLVideoElement | null;
            if (video_el) video_el.srcObject = null;
          }
          const numId = Number(msg.user_id);
          this.peer_connections.get(numId)?.close();
          this.peer_connections.delete(numId);
          this.streams.delete(numId);
        } else if (msg.type === 'answer') {
          await this.handle_answer(msg.from_user_id || msg.user_id, msg.sdp);
        } else if (msg.type === 'ice_candidate') {
          const pc = this.peer_connections.get(Number(msg.from_user_id));
          if (pc && msg.candidate) await pc.addIceCandidate(new RTCIceCandidate(msg.candidate));
        }
      } catch {}
    };

    this.ws.onerror = () => {};
  }

  async request_stream(user_id: number) {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    this.peer_connections.set(user_id, pc);

    pc.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        this.streams.set(user_id, event.streams[0]);
        this.attach_stream(user_id, event.streams[0]);
      }
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        this.ws?.send(JSON.stringify({ type: 'ice_candidate', candidate: e.candidate, target_type: 'evaluator', target_user_id: user_id, from_user_id: this.admin_user_id }));
      }
    };

    // Admin only receives — add recvonly transceiver so the offer includes a video m-line
    pc.addTransceiver('video', { direction: 'recvonly' });

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    this.ws?.send(JSON.stringify({ type: 'offer', target_user_id: user_id, sdp: offer, from_admin_id: this.admin_user_id }));
  }

  async handle_answer(user_id: number, sdp: any) {
    const pc = this.peer_connections.get(Number(user_id));
    if (pc) await pc.setRemoteDescription(new RTCSessionDescription(sdp));
  }

  get_video_src(user_id: number): MediaStream | null {
    return this.streams.get(user_id) || null;
  }

  async get_list(extra?: any) {
    try {
      const res: any = await this.lps.get_active_sessions({ ...this.params, ...extra });
      this.items = res;
      // Filtering re-renders the tiles, so the freshly created <video> elements are blank.
      // Re-bind the streams we already hold (and open any that are missing) once the DOM settles.
      this.sync_streams();
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Failed to fetch sessions', 'Error', 'error');
    }
  }

  // Keeps the *ngFor from destroying a tile (and its live <video>) when the list is replaced.
  track_by_user_id = (_: number, item: any) => this.get_item_user_id(item);

  // Re-attaches known MediaStreams to their video elements and requests streams for any
  // online evaluator we are not yet peered with. Safe to call as often as needed.
  sync_streams() {
    setTimeout(() => {
      (this.items?.data || []).forEach((item: any) => {
        const uid = this.get_item_user_id(item);
        if (!uid || this.get_item_status(item) !== 'online') return;

        const pc = this.peer_connections.get(uid);
        if (pc && ['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
          pc.close();
          this.peer_connections.delete(uid);
          this.streams.delete(uid);
        }

        const stream = this.streams.get(uid);
        if (stream) {
          this.attach_stream(uid, stream);
        } else if (!this.peer_connections.has(uid)) {
          this.request_stream(uid);
        }
      });
    });
  }

  attach_stream(user_id: number, stream: MediaStream) {
    const video_el = document.getElementById('video-' + user_id) as HTMLVideoElement | null;
    if (video_el && video_el.srcObject !== stream) {
      video_el.srcObject = stream;
      video_el.play().catch(() => {});
    }
  }

  apply_filters() {
    this.get_list();
  }

  on_university_change() {
    this.params.exam_id = null;
    this.params.subject_id = null;
    // Exams belong to a university — without this the dropdown offers other universities'
    // exams, which look selectable but can never match a session.
    this.filter_exam_options();
    this.apply_filters();
  }

  filter_exam_options() {
    const university_id = this.params.university_id;
    this.exam_options = university_id
      ? this.all_exam_options.filter((e: any) => e.university_id == university_id)
      : this.all_exam_options;
  }

  on_exam_change() {
    this.params.subject_id = null;
    this.apply_filters();
  }

  set_view_mode(mode: 'grid' | 'list') {
    this.view_mode = mode;
    // Switching back to grid rebuilds the tiles, so the videos need re-binding.
    if (mode === 'grid') this.sync_streams();
    if (mode === 'list') {
      const has_actions = this.columns.some((c: any) => c.headerName === 'Actions');
      if (!has_actions) {
        this.columns.push({
          headerName: 'Actions', sortable: false, filter: false,
          cellRenderer: ListActionButtonsComponent,
          headerClass: 'custom-header-center',
          cellStyle: { 'justify-content': 'center' },
          cellRendererParams: { actions: this.actions.bind(this) },
          editable: false, pinned: 'right', width: 90, minWidth: 90, maxWidth: 90,
        });
      }
    }
  }

  navigate_to_detail(user_id: number) {
    this.router.navigate(['/ai-admin/live-proctor', user_id]);
  }

  on_tile_click(item: any) {
    const uid = item.user_id || item.User?.id;
    if (uid) this.navigate_to_detail(uid);
  }

  request_tile_stream(item: any) {
    const uid = item.user_id || item.User?.id;
    if (uid && !this.peer_connections.has(uid)) {
      this.request_stream(uid);
    }
  }

  format_date(date: string): string {
    if (!date) return '-';
    const d = new Date(date);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const h12 = d.getHours() % 12 || 12;
    const min = String(d.getMinutes()).padStart(2, '0');
    const ampm = d.getHours() >= 12 ? 'pm' : 'am';
    return `${dd}/${mm}/${yyyy} ${h12}:${min}${ampm}`;
  }

  get_item_user_id(item: any): number {
    return item.user_id || item.User?.id || 0;
  }

  get_item_name(item: any): string {
    return item.User?.name || item.name || '-';
  }

  get_item_status(item: any): string {
    return (item.status || '').toLowerCase();
  }
}
